import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,

} from '@mui/material';
import Papa from 'papaparse';

interface FieldMapping {
  [key: string]: 'account_name' | 'notes' | 'ignore';
}

interface PreviewData {
  [key: string]: string;
}

import { Partner, uploadPartnerData, getPartnerAnalytics } from '../../api/partner';

export const PartnerUploadPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [isLoading, setIsLoading] = useState(false);

  const fetchPartners = async () => {
    try {
      setIsLoading(true);
      const data = await getPartnerAnalytics();
      setPartners(data);
      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const [partnerName, setPartnerName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping>({});
  const [previewData, setPreviewData] = useState<PreviewData[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      Papa.parse(selectedFile, {
        header: true,
        preview: 5,
        complete: (results) => {
          if (results.data.length > 0) {
            const headers = Object.keys(results.data[0] as Record<string, unknown>);
            setHeaders(headers);
            
            // Initialize field mappings
            const initialMappings: FieldMapping = {};
            headers.forEach(header => {
              initialMappings[header] = 'ignore';
            });
            setFieldMappings(initialMappings);
            
            // Set preview data
            setPreviewData(results.data as PreviewData[]);
          }
        },
      });
    }
  };

  const handleMappingChange = (header: string, value: 'account_name' | 'notes' | 'ignore') => {
    setFieldMappings(prev => ({
      ...prev,
      [header]: value,
    }));
  };

  const handleUpload = async () => {
    if (!file || !partnerName) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('partnerName', partnerName);
    formData.append('mappings', JSON.stringify(fieldMappings));

    try {
      if (!file) return;
      
      await uploadPartnerData(formData);

      // Clear form
      setPartnerName('');
      setFile(null);
      setHeaders([]);
      setFieldMappings({});
      setPreviewData([]);

      alert('Upload successful!');
      fetchPartners(); // Refresh the partner list
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex', 
      flexDirection: 'column',
      gap: 3,
      minHeight: '100vh',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <Typography variant="h4" gutterBottom>
        Partner Analytics
      </Typography>

      <Paper sx={{ 
        p: 3,
        width: '100%',
        minWidth: 'fit-content'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" color="textSecondary">
            Last refreshed: {lastRefreshed.toLocaleString()}
          </Typography>
          <Button
            variant="outlined"
            onClick={fetchPartners}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>

        <TableContainer sx={{ 
          width: '100%',
          overflowX: 'auto'
        }}>
          <Table sx={{ tableLayout: 'fixed', minWidth: '1000px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>Partner Name</TableCell>
                <TableCell sx={{ width: '20%' }} align="right">Account Count</TableCell>
                <TableCell sx={{ width: '40%' }}>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.map((partner) => (
                <TableRow key={partner.id}>
                  <TableCell>{partner.name}</TableCell>
                  <TableCell align="right">{partner.account_count}</TableCell>
                  <TableCell>
                    {new Date(partner.last_updated || partner.created_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
              {partners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No partners found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Typography variant="h4" gutterBottom>
        Upload Partner Account List
      </Typography>

      <Paper sx={{ 
        p: 3,
        width: '100%',
        minWidth: 'fit-content'
      }}>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Partner Name"
            value={partnerName}
            onChange={(e) => setPartnerName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            component="label"
            sx={{ mr: 2 }}
          >
            Choose CSV File
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleFileSelect}
            />
          </Button>

          {file && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Selected file: {file.name}
            </Typography>
          )}
        </Box>

        {headers.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Field Mapping
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="50%">CSV Header</TableCell>
                    <TableCell width="50%">Map To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {headers.map((header) => (
                    <TableRow key={header}>
                      <TableCell>{header}</TableCell>
                      <TableCell>
                        <FormControl fullWidth>
                          <Select
                            value={fieldMappings[header] || 'ignore'}
                            onChange={(e) => handleMappingChange(header, e.target.value as 'account_name' | 'notes' | 'ignore')}
                          >
                            <MenuItem value="account_name">Account Name</MenuItem>
                            <MenuItem value="notes">Notes</MenuItem>
                            <MenuItem value="ignore">Ignore</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!partnerName || !file}
              >
                Upload and Map
              </Button>
            </Box>
          </>
        )}
      </Paper>

      {headers.length > 0 && (
        <Paper sx={{ 
          p: 3,
          width: '100%',
          minWidth: 'fit-content'
        }}>
          <Typography variant="h6" gutterBottom>
            Preview
          </Typography>
          
          <TableContainer sx={{ 
            maxHeight: '400px',
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1
          }}>
            <Table stickyHeader sx={{ 
              '& th, & td': {
                minWidth: '200px',
                whiteSpace: 'nowrap',
                padding: '8px 16px'
              }
            }}>
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header} sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: (theme) => theme.palette.grey[100]
                    }}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewData.slice(0, 5).map((row, index) => (
                  <TableRow key={index}>
                    {headers.map((header) => (
                      <TableCell key={header}>{row[header]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};
